import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  type CommunityAnnouncementConfirmationAction,
  communityAnnouncementConfirmationCopy,
} from '@shared/dream-components';

import type { AnnouncementConfirmationResponse } from '~/services/api/community/getAnnouncementConfirmation.server';

import { ConfirmDialog } from './ConfirmDialog';

interface AnnouncementConfirmDialogProps {
  isOpen: boolean;
  confirmation: AnnouncementConfirmationResponse | null;
  channelName: string | null;
  confirmAction: CommunityAnnouncementConfirmationAction;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  afterLeave?: () => void;
}

export const AnnouncementConfirmDialog: FC<AnnouncementConfirmDialogProps> = ({
  isOpen,
  confirmation,
  channelName,
  confirmAction,
  isLoading = false,
  onConfirm,
  onCancel,
  afterLeave,
}) => {
  const { t } = useTranslation();

  if (!confirmation) return null;

  const { title, description, confirmLabel, recentUses } = communityAnnouncementConfirmationCopy({
    confirmation,
    channelName,
    confirmAction,
    translate: (key, values) => t(key, values),
  });

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
      afterLeave={afterLeave}
      isLoading={isLoading}
      body={
        recentUses.length > 0 && (
          <ul className="mt-4 flex flex-col gap-2">
            {recentUses.map(({ id, key, values }) => (
              <li
                key={id}
                className="flex items-start gap-2 rounded-2xl bg-black/[0.04] px-4 py-3 text-[13px] leading-[18px] text-black/70"
              >
                <ExclamationTriangleIcon className="mt-px size-4 shrink-0" aria-hidden="true" />
                <span>
                  <Trans
                    i18nKey={key}
                    count={values.count}
                    values={{ name: values.name }}
                    components={{ strong: <strong className="font-medium text-black" /> }}
                  />
                </span>
              </li>
            ))}
          </ul>
        )
      }
    />
  );
};
